/* eslint-disable react/no-array-index-key */
import {StabilityRuleFormOptions} from '@/modules/drug-catalog/constants'
import {getNewFormStabilityRules} from '@/modules/drug-catalog/functions'
import {StabilityRuleFormDialog} from '@/modules/drug-catalog/stability-rules/components'
import {
  getStabilityRuleFormDefaultValues,
  mapStabilityRuleFormToApiValues,
} from '@/modules/drug-catalog/stability-rules/functions'
import {StabilityRuleFormSchema} from '@/modules/drug-catalog/stability-rules/schemas'
import {dialog} from '@/modules/layout'
import {useSuspenseGetTradeName} from '@/modules/products/trade-names/api'
import {TradeNameConsumablesTab} from '@/modules/products/trade-names/components/trade-name-form/consumables-tab'
import {TradeNameEssentialSetupTab} from '@/modules/products/trade-names/components/trade-name-form/essential-setup-tab'
import {TradeNameLogisticsTab} from '@/modules/products/trade-names/components/trade-name-form/logistics-tab'
import {TradeNameStabilityRulesTab} from '@/modules/products/trade-names/components/trade-name-form/stability-rules-tab'
import {useSelectedDrugItem} from '@/modules/products/trade-names/components/trade-name-form/utils'
import {TradeNameFormDialogsIds} from '@/modules/products/trade-names/constants'
import {
  getMissingStabilityStages,
  getTradeNameFormDefaultValues,
  REQUIRED_STABILITY_STAGES,
} from '@/modules/products/trade-names/functions'
import {TradeNameFormSchema} from '@/modules/products/trade-names/schemas'
import {useZero} from '@/shared/integrations/zero/use-zero'
import {Badge, Box, HStack, Tabs} from '@chakra-ui/react'
import {CatalogType, StabilityRuleSource} from '@cypro/engine/database/schema'
import {AddTradeNameSchema} from '@cypro/engine/mutators'
import {LinkButton} from '@cypro/ui/components/ui/link-button'
import {LoadingLogo} from '@cypro/ui/components/ui/loading-logo'
import {useBlockerDialog} from '@cypro/ui/hooks/use-blocker-dialog'
import {useGuardNonexistentResourceForm} from '@cypro/ui/hooks/use-guard-nonexistent-resource-form'
import {focusFirstErrorField, setInitialFieldValues, useAppForm} from '@cypro/ui/integrations/form'
import {toaster} from '@cypro/ui/toaster'
import {isDefined} from '@cypro/ui/utils'
import {revalidateLogic, useStore} from '@tanstack/react-form'
import {useNavigate} from '@tanstack/react-router'
import {useTranslate} from '@tolgee/react'
import {FormEvent, useEffect, useState} from 'react'

function buildPayload(value: TradeNameFormSchema): AddTradeNameSchema | null {
  const stabilityRules = value.stabilityRules.map(mapStabilityRuleFormToApiValues)

  switch (value.type) {
    case CatalogType.enum.ACTIVE_SUBSTANCE:
      return {
        ...value,
        strengthUnitId: value.strengthUnitId,
        reconstitutedConcentrationUnitId: value.reconstitutedConcentrationUnitId || undefined,
        stabilityRules,
        reconstitutionSolvents: value.reconstitutionRequired ? value.reconstitutionSolvents : [],
      }

    case CatalogType.enum.SUPPORTIVE_MEDICATION:
      return {
        ...value,
        strengthUnitId: value.strengthUnitId,
        stabilityRules,
      }

    case CatalogType.enum.SOLUTION:
      return {
        ...value,
        concentrationUnitId: value.concentrationUnitId,
        stabilityRules,
      }

    default:
      return null
  }
}

const TABS = ['essential-setup', 'stability-rules', 'consumables', 'logistics'] as const
type TabId = (typeof TABS)[number]

interface Props {
  tradeNameId: string | undefined
  type?: CatalogType
}

export function TradeNameForm({tradeNameId, type: initialType}: Props) {
  const {t} = useTranslate('common')
  const {t: tTradeNames} = useTranslate('trade-names')

  const zero = useZero()
  const navigate = useNavigate()
  const [tradeName, queryState] = useSuspenseGetTradeName(tradeNameId)

  const initialValues = getTradeNameFormDefaultValues(tradeName)
  const isEditing = Boolean(tradeNameId)
  const [activeTab, setActiveTab] = useState<TabId>('essential-setup')
  const [showStabilityWarning, setShowStabilityWarning] = useState(false)

  const form = useAppForm({
    defaultValues: {...initialValues, type: initialType || initialValues.type} as TradeNameFormSchema,
    validators: {
      onDynamic: TradeNameFormSchema,
    },
    validationLogic: revalidateLogic(),
    onSubmit({value}) {
      const missingStages = getMissingStabilityStages({
        type: value.type,
        isDrySubstance: Boolean(value.isDrySubstance),
        ownRules: value.stabilityRules,
      })
      if (missingStages.length) {
        setShowStabilityWarning(true)
        setActiveTab('stability-rules')
        toaster.error({
          title: t('validation_error'),
          description: tTradeNames('missing_stability_rules'),
        })
        return
      }
      setShowStabilityWarning(false)

      const payload = buildPayload(value)
      if (!payload) throw new Error('Payload could not be determined.')

      if (tradeNameId) {
        return editTradeName({...payload, id: tradeNameId})
      }

      return createTradeName(payload)
    },
    listeners: {
      onChange({fieldApi, formApi}) {
        if (fieldApi.name === 'type') {
          formApi.setFieldValue('drugCatalogItemId', '')
          formApi.setFieldValue('medicationProfileId', '')
          formApi.setFieldValue('isDrySubstance', false)
          navigate({
            to: '.',
            from: '/configuration/products/trade-names/new',
            search: prev => ({...prev, type: fieldApi.state.value}),
            replace: true,
            ignoreBlocker: true,
          })
        }

        if (fieldApi.name === 'drugCatalogItemId') {
          formApi.setFieldValue('medicationProfileId', '')
          formApi.setFieldValue('isDrySubstance', false)
        }

        if (fieldApi.name === 'volumeValue' || fieldApi.name === 'overfilledBy') {
          const volumeValue = formApi.getFieldValue('volumeValue')
          const overfilledBy = formApi.getFieldValue('overfilledBy')

          if (!isDefined(volumeValue)) {
            formApi.setFieldValue('actualVolume', undefined)
          } else {
            const sum = Number(volumeValue) + Number(overfilledBy || 0)
            formApi.setFieldValue('actualVolume', sum)
          }
        }
      },
    },
    onSubmitInvalid({formApi}) {
      console.log(formApi)
    },
  })

  const stabilityRuleForm = useAppForm({
    ...StabilityRuleFormOptions,
    onSubmit({value, formApi, meta}) {
      form.setFieldValue(
        'stabilityRules',
        getNewFormStabilityRules(
          form.state.values.stabilityRules,
          {...value, source: StabilityRuleSource.enum.TRADE_NAME},
          meta.index
        )
      )
      formApi.reset()
      dialog.close(TradeNameFormDialogsIds.addStabilityRule)
    },
    onSubmitInvalid() {
      focusFirstErrorField()
    },
  })

  const isDefaultValue = useStore(form.store, state => state.isDefaultValue)

  const type = useStore(form.store, state => state.values.type)
  const drugCatalogItemId = useStore(form.store, state => state.values.drugCatalogItemId)
  const medicationProfileId = useStore(form.store, state => state.values.medicationProfileId)
  const isDrySubstance = useStore(form.store, state => state.values.isDrySubstance)
  const stabilityRules = useStore(form.store, state => state.values.stabilityRules)
  const [drug] = useSelectedDrugItem(type, drugCatalogItemId)
  const medicationProfile = drug?.medicationProfiles?.find(p => p.id === medicationProfileId)

  // recomputed from the live values so adding or removing a rule clears the warning
  const missingStabilityStages = showStabilityWarning
    ? getMissingStabilityStages({type, isDrySubstance: Boolean(isDrySubstance), ownRules: stabilityRules})
    : []

  // When creating an active-substance trade name, auto-fill the strength unit from
  // the selected substance's dose unit so the user doesn't have to re-enter it.
  useEffect(() => {
    if (isEditing || type !== CatalogType.enum.ACTIVE_SUBSTANCE || !drug?.activeSubstanceDetails?.doseUnitId) {
      return
    }

    setInitialFieldValues(form, {strengthUnitId: drug.activeSubstanceDetails.doseUnitId})
  }, [isEditing, type, drug, form])

  useBlockerDialog({shouldBlock: !isDefaultValue})
  useGuardNonexistentResourceForm({resource: tradeName, queryState, shouldGuard: isEditing})

  function createTradeName(payload: Parameters<typeof zero.mutators.tradeNames.add>[0]) {
    return zero.attemptMutation(zero.mutators.tradeNames.add(payload), async mutation => {
      const data = await mutation.server
      if (!data) return

      toaster.success({title: t('success'), description: t('trade_name_created_successfully')})
      form.reset()
      setActiveTab(TABS[0])
    })
  }

  function editTradeName(payload: Parameters<typeof zero.mutators.tradeNames.update>[0]) {
    return zero.attemptMutation(zero.mutators.tradeNames.update(payload), async mutation => {
      const data = await mutation.server
      if (!data) return

      toaster.success({title: t('success'), description: t('trade_name_updated_successfully')})
      navigate({to: '/configuration/products/trade-names', ignoreBlocker: true})
    })
  }

  function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    form.handleSubmit()
  }

  async function onAssignStabilityRule() {
    if (!medicationProfile) throw new Error(t('you_need_to_select_a_medicine_form'))

    const id = TradeNameFormDialogsIds.assignStabilityRule

    try {
      dialog.remove(id)
    } catch {}

    const {AssignStabilityRuleDialog} =
      await import('@/modules/products/trade-names/components/trade-name-form/assign-stability-rule-dialog')

    await dialog.open(id, {
      scrollBehavior: 'inside',
      children: <AssignStabilityRuleDialog form={form} medicationProfile={medicationProfile} />,
    })
  }

  async function onAddStabilityRule(index: number | undefined) {
    const id = TradeNameFormDialogsIds.addStabilityRule

    try {
      dialog.remove(id)
    } catch {}

    function onSubmit(event: FormEvent<HTMLFormElement>) {
      event.preventDefault()
      stabilityRuleForm.handleSubmit({index: isDefined(index) ? index : -1})
    }

    await dialog.open(id, {
      scrollBehavior: 'inside',
      size: 'cover',
      children: (
        <StabilityRuleFormDialog
          title={t('add_stability_rule_dialog.title')}
          description={t('add_stability_rule_dialog.description')}
          form={stabilityRuleForm}
          onSubmit={onSubmit}
          onCancel={() => dialog.close(id)}
        />
      ),
    })

    stabilityRuleForm.reset(getStabilityRuleFormDefaultValues(undefined))
  }

  function onEditStabilityRule(rule: StabilityRuleFormSchema, index: number) {
    if (rule.source !== StabilityRuleSource.enum.TRADE_NAME) {
      toaster.warning({
        title: t('you_cannot_edit_this_resource'),
        description: t('this_stability_rule_is_not_trade_name_specific'),
      })
      return
    }

    stabilityRuleForm.reset(rule)
    onAddStabilityRule(index)
  }

  function onDeleteStabilityRule(index: number) {
    // only active-substance trade names must keep the Open and Prepared rules
    if (type !== CatalogType.enum.ACTIVE_SUBSTANCE) {
      form.removeFieldValue('stabilityRules', index)
      return
    }

    const rule = form.getFieldValue(`stabilityRules[${index}]`) as StabilityRuleFormSchema
    const stage = rule?.stabilityStage?.[0]
    const requiredStages = REQUIRED_STABILITY_STAGES(form.getFieldValue('isDrySubstance'))
    if (stage && requiredStages.includes(stage)) {
      const ownCount = form.state.values.stabilityRules.filter(item => item.stabilityStage?.[0] === stage).length
      if (ownCount <= 1) {
        toaster.warning({title: tTradeNames('last_required_stability_rule')})
        return
      }
    }

    form.removeFieldValue('stabilityRules', index)
  }

  if (queryState.type !== 'complete') return <LoadingLogo />

  const submitLabel = !isEditing ? tTradeNames('create_trade_name') : t('save_changes')

  return (
    <form onSubmit={onSubmit}>
      <form.AppForm>
        <Tabs.Root value={activeTab} onValueChange={changes => setActiveTab(changes.value as TabId)}>
          <form.ErrorObserver />

          <Tabs.List>
            <Tabs.Trigger value="essential-setup">
              {tTradeNames('essential_setup')}
              <Box as="span" color="fg.error" marginLeft="1">
                *
              </Box>
            </Tabs.Trigger>
            <Tabs.Trigger value="stability-rules">
              {tTradeNames('stability_rules')}
              <Box as="span" color="fg.error" marginLeft="1">
                *
              </Box>
              {missingStabilityStages.length > 0 && (
                <Badge size="sm" colorPalette="red" marginLeft="1">
                  !
                </Badge>
              )}
            </Tabs.Trigger>
            <Tabs.Trigger value="consumables">{tTradeNames('consumables')}</Tabs.Trigger>
            <Tabs.Trigger value="logistics">{tTradeNames('logistics')}</Tabs.Trigger>
          </Tabs.List>

          <Tabs.Content value="essential-setup" paddingTop="6">
            <TradeNameEssentialSetupTab form={form} />
          </Tabs.Content>

          <Tabs.Content value="stability-rules" paddingTop="6">
            <TradeNameStabilityRulesTab
              form={form}
              missingStabilityStages={missingStabilityStages}
              onAssignStabilityRule={onAssignStabilityRule}
              onAddStabilityRule={onAddStabilityRule}
              onEditStabilityRule={onEditStabilityRule}
              onDeleteStabilityRule={onDeleteStabilityRule}
            />
          </Tabs.Content>

          <Tabs.Content value="consumables" paddingTop="6">
            <TradeNameConsumablesTab form={form} />
          </Tabs.Content>

          <Tabs.Content value="logistics" paddingTop="6">
            <TradeNameLogisticsTab form={form} />
          </Tabs.Content>

          <HStack paddingY="6" gapX="4" marginTop="6">
            <LinkButton to="/configuration/products/trade-names" variant="subtle">
              {t('cancel')}
            </LinkButton>

            <form.NextStepButton>{t('next_step')}</form.NextStepButton>

            <Box marginLeft="auto">
              <form.SubmitButton disabled={isEditing && isDefaultValue}>{submitLabel}</form.SubmitButton>
            </Box>
          </HStack>
        </Tabs.Root>
      </form.AppForm>
    </form>
  )
}
